import { useState } from 'react';
import { Dropdown, Empty, Spin, Tag, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OwnerType } from '@never2busy/shared';
import { keywordsApi, type Keyword } from '../../api/endpoints/keywords';

interface KeywordsManagerProps {
  ownerType: OwnerType;
  ownerId: number;
}

const { Text } = Typography;
const NO_CATEGORY = '__none';

/**
 * Etiquetas asignadas a una entidad (chips). Permite quitar con el ✕ y agregar
 * desde un dropdown agrupado por categoría. El single-select de cada categoría
 * lo resuelve el backend al asignar.
 */
export function KeywordsManager({ ownerType, ownerId }: KeywordsManagerProps): JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const assigned = useQuery({
    queryKey: ['keywords', 'for-owner', ownerType, ownerId],
    queryFn: () => keywordsApi.forOwner(ownerType, ownerId),
  });

  const catalog = useQuery({
    queryKey: ['keywords', 'list', ownerType],
    queryFn: () => keywordsApi.list(ownerType),
    enabled: open,
  });

  const categories = useQuery({
    queryKey: ['keywords', 'categories', ownerType],
    queryFn: () => keywordsApi.listCategories(ownerType),
    enabled: open,
  });

  const toggle = useMutation({
    mutationFn: (keywordId: number) => keywordsApi.toggle(keywordId, ownerType, ownerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['keywords', 'for-owner', ownerType] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['persons'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
    },
    onError: () => message.error('No se pudo actualizar la etiqueta'),
  });

  if (assigned.isLoading) {
    return <Spin size="small" />;
  }

  const assignedIds = new Set((assigned.data ?? []).map((k) => k.id));
  const available = (catalog.data ?? []).filter((k) => !assignedIds.has(k.id));

  // Agrupa las opciones disponibles por categoría (menú del dropdown).
  const cats = [...(categories.data ?? [])].sort((a, b) => a.sort - b.sort);
  const catName = new Map(cats.map((c) => [c.id, c.name]));
  const groups = new Map<string, Keyword[]>();
  for (const k of available) {
    const key = k.categoryId != null ? String(k.categoryId) : NO_CATEGORY;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(k);
  }

  const onPick = (id: number): void => {
    toggle.mutate(id);
    setOpen(false);
  };

  const orderedKeys = [
    ...cats.map((c) => String(c.id)).filter((k) => groups.has(k)),
    ...(groups.has(NO_CATEGORY) ? [NO_CATEGORY] : []),
  ];

  const items: MenuProps['items'] =
    available.length === 0
      ? [
          {
            key: 'none',
            label: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin más" />,
            disabled: true,
          },
        ]
      : orderedKeys.map((gkey) => ({
          key: `g-${gkey}`,
          type: 'group' as const,
          label: gkey === NO_CATEGORY ? 'Sin categoría' : (catName.get(Number(gkey)) ?? 'Categoría'),
          children: (groups.get(gkey) ?? []).map((k) => ({
            key: String(k.id),
            label: k.text,
            onClick: () => onPick(k.id),
          })),
        }));

  return (
    <div>
      {(assigned.data ?? []).length === 0 && (
        <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
          Sin etiquetas
        </Text>
      )}
      {(assigned.data ?? []).map((k: Keyword) => (
        <Tag
          key={k.id}
          color="blue"
          closable
          onClose={(e) => {
            e.preventDefault();
            toggle.mutate(k.id);
          }}
          style={{ marginBottom: 4 }}
        >
          {k.text}
        </Tag>
      ))}

      <Dropdown open={open} onOpenChange={setOpen} trigger={['click']} menu={{ items }}>
        <Tag style={{ cursor: 'pointer', borderStyle: 'dashed' }}>
          <PlusOutlined /> Etiqueta
        </Tag>
      </Dropdown>
    </div>
  );
}
